import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building, Edit3, LogOut, Save, ArrowLeft, AlertCircle, XCircle, X,
  Phone, Mail, Users, FileText, Loader2, CreditCard, Upload, Download, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { resolveFileUrl } from '../../utils/fileUrl';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const InstitutionalDashboardPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [newConstitutionFile, setNewConstitutionFile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('institutionalToken');
    if (!token) {
      toast.error('Please login first');
      navigate('/auth?tab=institutional');
      return;
    }
    fetchProfile(token);
  }, [navigate]);

  const fetchProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/institutional/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        initEditData(data);
      } else if (response.status === 401) {
        localStorage.removeItem('institutionalToken');
        toast.error('Session expired. Please login again.');
        navigate('/auth?tab=institutional');
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const initEditData = (data) => {
    setEditData({
      sansthaName: data.sansthaName || '',
      address: data.address || '',
      registrationNumber: data.registrationNumber || '',
      numberOfFamilies: data.numberOfFamilies || '',
      contactPhone: data.contactPhone || '',
      sansthaEmail: data.sansthaEmail || '',
      officeBarers: data.officeBarers || [],
      hasConstitution: data.hasConstitution || '',
      otherInfo: data.otherInfo || '',
      paymentAmount: data.paymentAmount || '',
      paymentMode: data.paymentMode || '',
      paymentDate: data.paymentDate || '',
      transactionNumber: data.transactionNumber || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('institutionalToken');
      let response;

      if (newConstitutionFile) {
        const formData = new FormData();
        formData.append('formData', JSON.stringify(editData));
        formData.append('constitutionFile', newConstitutionFile);
        response = await fetch(`${API_URL}/api/institutional/me`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      } else {
        response = await fetch(`${API_URL}/api/institutional/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editData)
        });
      }

      if (response.ok) {
        const data = await response.json();
        setProfile(data.institution);
        initEditData(data.institution);
        setEditMode(false);
        setNewConstitutionFile(null);
        if (data.institution?.status === 'pending') {
          toast.success('Profile updated! Changes will reflect after admin approval.');
        } else {
          toast.success('Profile updated successfully!');
        }
      } else if (response.status === 401) {
        localStorage.removeItem('institutionalToken');
        toast.error('Session expired. Please login again.');
        navigate('/auth?tab=institutional');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('institutionalToken');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleOfficeBearerChange = (index, field, value) => {
    setEditData(prev => {
      const updated = [...prev.officeBarers];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, officeBarers: updated };
    });
  };

  const addOfficeBearer = () => {
    setEditData(prev => ({
      ...prev,
      officeBarers: [...(prev.officeBarers || []), { designation: '', name: '', contact: '' }]
    }));
  };

  const removeOfficeBearer = (index) => {
    setEditData(prev => ({
      ...prev,
      officeBarers: prev.officeBarers.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="institutional-dashboard">
      {/* Header */}
      <div className="gradient-orange py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/80 hover:text-white">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Building className="w-6 h-6" />
                  Institution Dashboard
                </h1>
                <p className="text-white/80 text-sm">{profile.sansthaName}</p>
              </div>
            </div>
            <Button variant="ghost" className="text-white hover:bg-white/20" onClick={handleLogout} data-testid="institutional-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banners */}
        {profile.status === 'pending' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3" data-testid="institutional-status-pending">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-800">Registration Under Review</p>
              <p className="text-sm text-yellow-700">Your changes are pending admin approval. They will reflect on the portal after approval.</p>
            </div>
          </div>
        )}
        {profile.status === 'rejected' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="institutional-status-rejected">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">Registration Rejected</p>
              <p className="text-sm text-red-700">Your registration was rejected. Please edit your details and save to resubmit for approval.</p>
            </div>
          </div>
        )}

        <Card className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Institution Details
            </h2>
            <div className="flex gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); initEditData(profile); }} data-testid="cancel-edit-btn">Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-institutional-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button onClick={() => setEditMode(true)} data-testid="edit-institutional-btn">
                  <Edit3 className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-500 text-sm">Institution Name / संस्था का नाम</Label>
                {editMode ? (
                  <Input name="sansthaName" value={editData.sansthaName} onChange={handleChange} className="mt-1" />
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.sansthaName || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Registration Number / पंजीकरण संख्या</Label>
                {editMode ? (
                  <Input name="registrationNumber" value={editData.registrationNumber} onChange={handleChange} className="mt-1" />
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.registrationNumber || '-'}</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-gray-500 text-sm">Address / पता</Label>
              {editMode ? (
                <Textarea name="address" value={editData.address} onChange={handleChange} rows={2} className="mt-1" />
              ) : (
                <p className="font-medium text-gray-900 mt-1">{profile.address || '-'}</p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-500 text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> Contact Phone / संपर्क फ़ोन</Label>
                {editMode ? (
                  <Input name="contactPhone" value={editData.contactPhone} onChange={handleChange} className="mt-1" />
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.contactPhone || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> Email / ईमेल</Label>
                {editMode ? (
                  <Input name="sansthaEmail" value={editData.sansthaEmail} onChange={handleChange} className="mt-1" />
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.sansthaEmail || '-'}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-500 text-sm flex items-center gap-1"><Users className="w-3 h-3" /> Number of Families / परिवारों की संख्या</Label>
                {editMode ? (
                  <Input name="numberOfFamilies" value={editData.numberOfFamilies} onChange={handleChange} className="mt-1" />
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.numberOfFamilies || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Login Email / लॉगिन ईमेल</Label>
                <p className="font-medium text-gray-900 mt-1">{profile.loginEmail || '-'}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-500 text-sm">Has Constitution / संविधान है</Label>
                {editMode ? (
                  <select name="hasConstitution" value={editData.hasConstitution} onChange={handleChange} className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select</option>
                    <option value="yes">Yes / हाँ</option>
                    <option value="no">No / नहीं</option>
                  </select>
                ) : (
                  <p className="font-medium text-gray-900 mt-1">{profile.hasConstitution === 'yes' ? 'Yes / हाँ' : profile.hasConstitution === 'no' ? 'No / नहीं' : '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-sm flex items-center gap-1"><FileText className="w-3 h-3" /> Constitution File / संविधान फाइल</Label>
                {editMode ? (
                  <div className="mt-1">
                    {(profile.constitutionFile || newConstitutionFile) && (
                      <div className="mb-2 flex items-center gap-2 text-sm">
                        {newConstitutionFile ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" /> {newConstitutionFile.name} (new)
                            <button onClick={() => setNewConstitutionFile(null)} className="text-red-500 ml-1 hover:underline text-xs">Remove</button>
                          </span>
                        ) : profile.constitutionFile ? (
                          <a href={resolveFileUrl(profile.constitutionFile)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" /> View current file
                          </a>
                        ) : null}
                      </div>
                    )}
                    <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid="constitution-file-upload">
                      <Upload className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">{newConstitutionFile ? 'Change file' : 'Upload new constitution file'}</span>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => setNewConstitutionFile(e.target.files[0])} />
                    </label>
                  </div>
                ) : (
                  <div className="mt-1">
                    {profile.constitutionFile ? (
                      <a href={resolveFileUrl(profile.constitutionFile)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm" data-testid="view-constitution-file">
                        <Download className="w-4 h-4" /> View / Download Constitution
                      </a>
                    ) : (
                      <p className="text-gray-400 text-sm mt-1">No file uploaded</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Office Bearers */}
            <div>
              <Label className="text-gray-500 text-sm mb-2 block">Office Bearers / पदाधिकारी</Label>
              {editMode ? (
                <div className="space-y-3">
                  {editData.officeBarers?.length === 0 && (
                    <p className="text-sm text-gray-400 italic">No office bearers added. Click "Add Bearer" to add.</p>
                  )}
                  {editData.officeBarers?.map((ob, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 bg-gray-50 p-3 rounded-lg items-end">
                      <div>
                        <Label className="text-xs text-gray-500">Designation / पद</Label>
                        <Input value={ob.designation || ''} onChange={(e) => handleOfficeBearerChange(i, 'designation', e.target.value)} className="h-8 text-sm mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Name / नाम</Label>
                        <Input value={ob.name || ''} onChange={(e) => handleOfficeBearerChange(i, 'name', e.target.value)} className="h-8 text-sm mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Contact / संपर्क</Label>
                        <Input value={ob.contact || ob.mobile || ''} onChange={(e) => handleOfficeBearerChange(i, 'contact', e.target.value)} className="h-8 text-sm mt-1" />
                      </div>
                      <button type="button" onClick={() => removeOfficeBearer(i)} className="text-red-500 hover:text-red-700 h-8 w-8 flex items-center justify-center rounded hover:bg-red-50" data-testid={`user-remove-bearer-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={addOfficeBearer} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors" data-testid="user-add-bearer-btn">
                    + Add Office Bearer / पदाधिकारी जोड़ें
                  </button>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {profile.officeBarers && profile.officeBarers.length > 0 ? (
                    profile.officeBarers.map((ob, i) => (
                      <div key={i} className="flex gap-4 text-sm bg-gray-50 p-3 rounded-lg">
                        <span className="font-medium">{ob.name || '-'}</span>
                        <span className="text-gray-500">{ob.designation}</span>
                        <span className="text-gray-500">{ob.contact || ob.mobile || '-'}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No office bearers added</p>
                  )}
                </div>
              )}
            </div>

            {/* Other Info */}
            <div>
              <Label className="text-gray-500 text-sm">Other Information / अन्य जानकारी</Label>
              {editMode ? (
                <Textarea name="otherInfo" value={editData.otherInfo} onChange={handleChange} rows={3} className="mt-1" />
              ) : (
                <p className="font-medium text-gray-900 mt-1">{profile.otherInfo || '-'}</p>
              )}
            </div>

            {/* Payment Details */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment Details / भुगतान विवरण
              </h3>
              <div className="grid md:grid-cols-2 gap-6 bg-green-50 p-4 rounded-lg">
                <div>
                  <Label className="text-gray-500 text-sm">Amount / राशि (₹)</Label>
                  {editMode ? (
                    <Input name="paymentAmount" value={editData.paymentAmount} onChange={handleChange} className="mt-1 bg-white" />
                  ) : (
                    <p className="font-medium text-gray-900 mt-1">{profile.paymentAmount ? `₹${profile.paymentAmount}` : '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Payment Mode / भुगतान माध्यम</Label>
                  {editMode ? (
                    <select name="paymentMode" value={editData.paymentMode} onChange={handleChange} className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                      <option value="">Select</option>
                      <option value="Online">Online</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  ) : (
                    <p className="font-medium text-gray-900 mt-1">{profile.paymentMode || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Payment Date / भुगतान तिथि</Label>
                  {editMode ? (
                    <Input name="paymentDate" type="date" value={editData.paymentDate} onChange={handleChange} className="mt-1 bg-white" />
                  ) : (
                    <p className="font-medium text-gray-900 mt-1">{profile.paymentDate || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Transaction Number / लेन-देन संख्या</Label>
                  {editMode ? (
                    <Input name="transactionNumber" value={editData.transactionNumber} onChange={handleChange} className="mt-1 bg-white" />
                  ) : (
                    <p className="font-medium text-gray-900 mt-1">{profile.transactionNumber || '-'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default InstitutionalDashboardPage;
